import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';
import { ElementType, FC, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import {
  IcBlockIcon,
  IcDeleteIcon,
  IcLogOutIcon,
  IcPreviewIcon,
  IcPrivacyIcon,
  IcReportIcon,
  IcSEditIcon,
  IcUsersIcon,
} from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';
import { PopupStore } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { ChangeSpaceVisibilityPopup } from 'APP/main/PopupManager/Popups/ChangeSpaceVisibilityPopup/ChangeSpaceVisibilityPopup';
import { EditSpacePopup } from 'APP/main/PopupManager/Popups/EditSpacePopup/EditSpacePopup';
import { ReportSpacePopup } from 'APP/main/PopupManager/Popups/ReportSpacePopup/ReportSpacePopup';
import { SpaceBlackList } from 'APP/main/PopupManager/Popups/SpaceBlackList/SpaceBlackList';
import { SpaceMembersPopup } from 'APP/main/PopupManager/Popups/SpaceMembersPopup/SpaceMembersPopup';
import { SpacePermissionsPopup } from 'APP/main/PopupManager/Popups/SpacePermissionsPopup/SpacePermissionsPopup';
import { useSpaceActions } from 'APP/main/hooks/space/useSpaceActions';
import { Features } from 'APP/packages/features/Features';
import { useNavigateTo } from 'APP/router/hooks/useNavigateTo';
import { Space } from 'STORE/Spaces/Space';

interface ISpaceMenuItem {
  key: string;
  title: string;
  icon: ElementType;
  count?: number;
  onClick(): void;
}

interface ISpaceProfilePresenterParams {
  space: Space;
  popupInstance?: PopupStore;
}

interface ISpaceProfilePresenter {
  childPopup: PopupStore | null;
  menu: ISpaceMenuItem[];
  actions: ISpaceMenuItem[];
  canUpdateDeepLink: boolean;
  isShowOnboarding: boolean;
  onEditSpace(): void;
  onShowMembers(): void;
  onShowBlackList(): void;
  onShowPermissions(): void;
  onReportSpace(): void;
  onUpdateDeepLink(): Promise<void>;
  onChangeSpaceVisibility(): void;
  openChildPopup<T>(component: FC<ICommonPopupsProps<T>>, params: T): void;
}

export function useSpaceProfilePresenter({
  space,
  popupInstance,
}: ISpaceProfilePresenterParams): ISpaceProfilePresenter {
  const { t } = useTranslation();
  const navigateTo = useNavigateTo();
  const location = useLocation();
  const source = useAsObservableSource({ space, location, navigateTo });
  const spaceActions = useSpaceActions(space, popupInstance);

  const presenter = useLocalStore<ISpaceProfilePresenter>(() => ({
    childPopup: null,

    get menu(): ISpaceMenuItem[] {
      return [
        {
          key: 'members-space',
          title: t('space_members'),
          count: source.space.participantsCount,
          icon: IcUsersIcon,
          isVisible: source.space.isOwner || source.space.isAdmin,
          onClick: presenter.onShowMembers,
        },
        {
          key: 'balck-list-space',
          title: t('balck_list_title'),
          icon: IcBlockIcon,
          isVisible: source.space.isOwner,
          onClick: presenter.onShowBlackList,
        },
        {
          key: 'visibility-space',
          title: t('permissions_label'),
          icon: IcPrivacyIcon,
          isVisible:
            Features.isPermissionsSpaceEnabled() &&
            source.space.isPublic &&
            (source.space.isOwner || source.space.isAdmin),
          onClick: presenter.onShowPermissions,
        },
      ].filter((item) => item.isVisible);
    },

    get actions(): ISpaceMenuItem[] {
      return [
        {
          key: 'edit-space',
          title: t('space_edit_space'),
          icon: IcSEditIcon,
          isVisible: source.space.isOwner || source.space.isAdmin,
          onClick: presenter.onEditSpace,
        },
        {
          key: 'visibility-space',
          title: t('space_visibility_settings'),
          icon: IcPreviewIcon,
          isVisible: !source.space.isGuest,
          onClick: presenter.onChangeSpaceVisibility,
        },
        {
          key: 'report-space',
          title: t('common_report'),
          icon: IcReportIcon,
          isVisible: Features.isReportsEnabled() && source.space.isMember,
          onClick: presenter.onReportSpace,
        },
        {
          key: 'leave-space',
          title: t('space_leave_space'),
          icon: IcLogOutIcon,
          isVisible: spaceActions.canLeave,
          onClick: spaceActions.onLeave,
        },
        {
          key: 'delete-space',
          title: t('space_delete_space'),
          icon: IcDeleteIcon,
          isVisible: spaceActions.canDelete,
          onClick: spaceActions.onDelete,
        },
      ].filter((item) => item.isVisible);
    },

    get canUpdateDeepLink(): boolean {
      return (source.space.isOwner || source.space.isAdmin) && source.space.isPublic;
    },

    get isShowOnboarding(): boolean {
      return source.space.isOwner;
    },

    onShowMembers(): void {
      presenter.openChildPopup(SpaceMembersPopup, { spaceId: source.space.id });
    },

    onShowBlackList(): void {
      presenter.openChildPopup(SpaceBlackList, { spaceId: source.space.id });
    },

    onShowPermissions(): void {
      presenter.openChildPopup(SpacePermissionsPopup, { spaceId: source.space.id });
    },

    onEditSpace(): void {
      presenter.openChildPopup(EditSpacePopup, { spaceId: source.space.id });
    },

    onReportSpace(): void {
      presenter.openChildPopup(ReportSpacePopup, { spaceId: source.space.id });
    },

    async onUpdateDeepLink(): Promise<void> {
      const result = await Tasks.app.showConfirm({
        text: t('new_deeplink_confirm_message'),
        primaryText: t('common_yes'),
        secondaryText: t('common_cancel'),
      });

      if (result) {
        await Tasks.space.updateDeepLink(source.space.id);
      }
    },

    onChangeSpaceVisibility(): void {
      presenter.openChildPopup(ChangeSpaceVisibilityPopup, { spaceId: source.space.id });
    },

    openChildPopup<T>(component: FC<ICommonPopupsProps<T>>, params: T): void {
      popupInstance?.hide();

      presenter.childPopup = Tasks.app.openPopup(component, {
        params,
        onClose: () => popupInstance?.close(),
        onBack: () => popupInstance?.show(),
      });
    },
  }));

  useEffect(() => {
    return () => presenter.childPopup?.close();
  }, []);

  return presenter;
}
